import React, { useEffect, useState, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { apiService } from '../services/api';
import { formatDate } from '../lib/utils';

interface CustomerInvoice {
  documentNumber: string;
  customerNumber: string;
  customerName: string;
  amount: number;
  balance: number;
  currency: string;
  dueDate: string;
  invoiceDate: string;
  booked: boolean;
  ocr: string | null;
}

interface InvoiceLine {
  rowId: number;
  articleNumber: string | null;
  description: string | null;
  quantity: number | null;
  unit: string | null;
  price: number | null;
  discount: number | null;
  total: number | null;
  vat: number | null;
}

export function CustomerInvoices() {
  const [invoices, setInvoices] = useState<CustomerInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [lines, setLines] = useState<Record<string, InvoiceLine[]>>({});
  const [loadingLines, setLoadingLines] = useState<Set<string>>(new Set());

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiService.getCustomerInvoices();
      setInvoices(data.invoices);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const toggleExpand = async (docNum: string) => {
    const isExpanded = expanded.has(docNum);
    const next = new Set(expanded);

    if (isExpanded) {
      next.delete(docNum);
      setExpanded(next);
      return;
    }

    next.add(docNum);
    setExpanded(next);

    if (lines[docNum]) return;

    setLoadingLines((prev) => new Set(prev).add(docNum));
    try {
      const data = await apiService.getCustomerInvoiceLines(docNum);
      setLines((prev) => ({ ...prev, [docNum]: data.lines }));
    } catch {
      setLines((prev) => ({ ...prev, [docNum]: [] }));
    } finally {
      setLoadingLines((prev) => {
        const next = new Set(prev);
        next.delete(docNum);
        return next;
      });
    }
  };

  const isOverdue = (dueDate: string) => dueDate && new Date(dueDate) < new Date();

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Customer Invoices</h1>
            <p className="text-sm text-muted-foreground mt-1">Outstanding invoices pending reconciliation.</p>
          </div>
          <button
            onClick={loadInvoices}
            disabled={loading}
            className="h-9 px-3 rounded-md border border-border/60 bg-white text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-2.5">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <div className="bg-[#fafafa] border border-border/60 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 bg-muted/60">
                <th className="p-3 w-8" />
                <th className="p-3 text-left font-medium text-muted-foreground">Customer</th>
                <th className="p-3 text-left font-medium text-muted-foreground">Invoice #</th>
                <th className="p-3 text-right font-medium text-muted-foreground">Amount</th>
                <th className="p-3 text-right font-medium text-muted-foreground">Balance</th>
                <th className="p-3 text-left font-medium text-muted-foreground">Invoice Date</th>
                <th className="p-3 text-left font-medium text-muted-foreground">Due Date</th>
                <th className="p-3 text-left font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-muted-foreground text-sm">
                    Loading invoices…
                  </td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-muted-foreground text-sm">
                    No unpaid customer invoices found
                  </td>
                </tr>
              ) : (
                invoices.map((inv) => {
                  const isOpen = expanded.has(inv.documentNumber);
                  const isLoadingLines = loadingLines.has(inv.documentNumber);
                  const invLines = lines[inv.documentNumber];
                  const overdue = isOverdue(inv.dueDate);

                  return (
                    <React.Fragment key={inv.documentNumber}>
                      <tr
                        className="border-b border-border/40 last:border-0 hover:bg-muted/20 cursor-pointer transition-colors"
                        onClick={() => toggleExpand(inv.documentNumber)}
                      >
                        <td className="p-3 text-center text-muted-foreground select-none text-xs">
                          {isOpen ? '▾' : '▸'}
                        </td>
                        <td className="p-3 font-medium">{inv.customerName}</td>
                        <td className="p-3 text-muted-foreground font-mono text-xs">{inv.documentNumber}</td>
                        <td className="p-3 text-right font-mono text-xs">
                          {inv.amount.toLocaleString('sv-SE', { minimumFractionDigits: 2 })} {inv.currency}
                        </td>
                        <td className="p-3 text-right font-mono text-xs">
                          {inv.balance.toLocaleString('sv-SE', { minimumFractionDigits: 2 })} {inv.currency}
                        </td>
                        <td className="p-3 text-muted-foreground text-xs">{formatDate(inv.invoiceDate)}</td>
                        <td className={`p-3 text-xs font-medium ${overdue ? 'text-destructive' : 'text-muted-foreground'}`}>
                          {formatDate(inv.dueDate)}
                        </td>
                        <td className="p-3">
                          {overdue ? (
                            <span className="inline-flex items-center text-xs bg-red-50 text-red-600 border border-red-100 px-2 py-0.5 rounded-full">
                              Overdue
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-xs bg-amber-50 text-amber-600 border border-amber-100 px-2 py-0.5 rounded-full">
                              Unpaid
                            </span>
                          )}
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="border-b border-border/40 bg-muted/10">
                          <td colSpan={8} className="px-10 py-4">
                            {isLoadingLines ? (
                              <p className="text-xs text-muted-foreground">Loading lines…</p>
                            ) : !invLines || invLines.length === 0 ? (
                              <p className="text-xs text-muted-foreground">No line items available.</p>
                            ) : (
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b border-border/40 text-muted-foreground">
                                    <th className="pb-2 text-left font-medium">Article</th>
                                    <th className="pb-2 text-left font-medium">Description</th>
                                    <th className="pb-2 text-right font-medium">Qty</th>
                                    <th className="pb-2 text-left font-medium">Unit</th>
                                    <th className="pb-2 text-right font-medium">Price</th>
                                    <th className="pb-2 text-right font-medium">Disc %</th>
                                    <th className="pb-2 text-right font-medium">VAT %</th>
                                    <th className="pb-2 text-right font-medium">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {invLines.map((line) => (
                                    <tr key={line.rowId} className="border-b border-border/30 last:border-0">
                                      <td className="py-2 text-muted-foreground">{line.articleNumber ?? '—'}</td>
                                      <td className="py-2">{line.description ?? '—'}</td>
                                      <td className="py-2 text-right font-mono">{line.quantity ?? '—'}</td>
                                      <td className="py-2 text-muted-foreground">{line.unit ?? '—'}</td>
                                      <td className="py-2 text-right font-mono">
                                        {line.price != null ? line.price.toLocaleString('sv-SE', { minimumFractionDigits: 2 }) : '—'}
                                      </td>
                                      <td className="py-2 text-right font-mono">{line.discount ?? '—'}</td>
                                      <td className="py-2 text-right font-mono">{line.vat ?? '—'}</td>
                                      <td className="py-2 text-right font-mono">
                                        {line.total != null ? line.total.toLocaleString('sv-SE', { minimumFractionDigits: 2 }) : '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
